import {
  createClient,
  type Client,
} from '@data-access/connect/tc-issue-management';
import { useQueryClient } from '@tanstack/solid-query';
import {
  Component,
  createContext,
  createResource,
  JSX,
  Show,
  useContext,
} from 'solid-js';
import { IssueApiService } from './api-services/issue-api-service';
import { IssueReferenceApiService } from './api-services/issue-link-api-service';
import { CommentApiService } from './api-services/tcps-comment-api-service';
import { ViewApiService } from './api-services/tcps-view-api-service';
import { TagApiService } from './api-services/tcps-tag-api-service';
import { FileApiService } from './api-services/tcps-file-api-service';
import { EcomApiService } from './api-services/ecom-api-service';
import { UserApiService } from './api-services/user-api-service';
import { FollowApiService } from './api-services/follow-api-service';
import { TcpsGroupApiService } from './api-services/tcps-group-api-service';
import { ObjectLinkApiService } from './api-services/tcps-object-link-service';
import { TcpsProjectApiService } from './api-services/tcps-project-api-service';
import { IssueExportApiService } from './api-services/issue-export-api-service';
import { useConfiguration } from './ConfigurationProvider';

interface ApiServices {
  issueApi: IssueApiService;
  issueReferenceApi: IssueReferenceApiService;
  commentApi: CommentApiService;
  viewApi: ViewApiService;
  tagApi: TagApiService;
  fileApi: FileApiService;
  ecomApi: EcomApiService;
  userApi: UserApiService;
  followApi: FollowApiService;
  tcpsGroupApi: TcpsGroupApiService;
  objectLinkApi: ObjectLinkApiService;
  tcpsProjectApi: TcpsProjectApiService;
  issueExportApi: IssueExportApiService;
  issueManagementClient: Client;
}

interface ApiServiceContextValue {
  services: ApiServices;
}

const ApiServiceContext = createContext<ApiServiceContextValue>();

export interface ApiServiceProviderProps {
  children?: JSX.Element;
  accessToken: string;
  projectId: string;
  location?: string;
}

export const ApiServiceProvider: Component<ApiServiceProviderProps> = (
  props: ApiServiceProviderProps
) => {
  const queryClient = useQueryClient();
  const configuration = useConfiguration();

  // Make the resource reactive to environment and location changes
  const [clients] = createResource(
    () => ({
      environment: configuration.currentEnvironment(),
      location: props.location,
    }),
    // eslint-disable-next-line solid/reactivity
    () => {
      if (props.accessToken.length === 0) {
        throw new Error('Access token is empty');
      }

      // Use regional URLs when project location is available
      const issueManagementDomain =
        configuration.getRegionalIssueManagementDomain(props.location);
      const tcpsApiDomain = configuration.getRegionalTcpsApiDomain(
        props.location
      );
      const userApiDomain = configuration.getRegionalUserApiDomain(
        props.location
      );
      const followApiDomain = configuration.getRegionalIssueManagementDomain(
        props.location
      );
      const ecomApiDomain = configuration.getEcomApiDomain(); // Non-regional for now
      const bcfApiDomain = configuration.getRegionalBcfDomain(props.location);

      const issueManagementClient = configureClient(
        props.accessToken,
        issueManagementDomain
      );
      const tcpsClient = configureClient(props.accessToken, tcpsApiDomain);
      const userClient = configureClient(props.accessToken, userApiDomain);
      const followClient = configureClient(props.accessToken, followApiDomain);
      const issueApi = new IssueApiService(issueManagementClient, queryClient);
      const issueReferenceApi = new IssueReferenceApiService(
        issueManagementClient,
        queryClient,
        tcpsClient
      );
      const commentApi = new CommentApiService(
        issueManagementClient,
        queryClient
      );
      const ecomClient = configureClient(props.accessToken, ecomApiDomain);
      const viewApi = new ViewApiService(tcpsClient, queryClient);
      const tagApi = new TagApiService(
        tcpsClient,
        queryClient,
        configuration.getBatchApiDomain,
        props.accessToken
      );
      const fileApi = new FileApiService(tcpsClient, queryClient);
      const ecomApi = new EcomApiService(ecomClient, queryClient);
      const userApi = new UserApiService(userClient, queryClient);
      const followApi = new FollowApiService(
        followClient,
        queryClient,
        configuration.currentEnvironment
      );
      const tcpsGroupApi = new TcpsGroupApiService(tcpsClient, queryClient);
      const objectLinkApi = new ObjectLinkApiService(tcpsClient, queryClient);
      const tcpsProjectApi = new TcpsProjectApiService(tcpsClient, queryClient);
      const bcfClient = configureClient(props.accessToken, bcfApiDomain);
      const issueExportApi = new IssueExportApiService(bcfClient, queryClient);

      return {
        issueApi,
        issueReferenceApi,
        commentApi,
        viewApi,
        tagApi,
        fileApi,
        ecomApi,
        userApi,
        followApi,
        tcpsGroupApi,
        objectLinkApi,
        tcpsProjectApi,
        issueExportApi,
        issueManagementClient,
      };
    }
  );

  return (
    <Show when={clients()}>
      <ApiServiceContext.Provider value={{ services: clients()! }}>
        {props.children}
      </ApiServiceContext.Provider>
    </Show>
  );
};

export const useApiServices = (): ApiServices => {
  const context = useContext(ApiServiceContext);
  if (context === undefined) {
    throw new Error('useApiServices must be used within a ApiServiceProvider');
  }

  return context.services;
};

const configureClient = (accessToken: string, baseUrl: string): Client => {
  const client = createClient({ baseUrl });

  client.interceptors.request.use((request) => {
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    request.headers.set('Content-Type', 'application/json');
    return request;
  });

  return client;
};
